import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';

import List from '@material-ui/core/List';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';

import CreditCardIcon from '@material-ui/icons/CreditCard';
import ReceiptIcon from '@material-ui/icons/Receipt';

import ChargePaymentMethodDialog from 'app/components/accounting/payments/ChargePaymentMethodDialog';
import SavePaymentMethodDialog from 'app/components/accounting/payments/SavePaymentMethodDialog';
import EditPaymentMethodDialog from 'app/components/accounting/payments/EditPaymentMethodDialog';
import PaymentMethodItem from 'app/components/accounting/payments/PaymentMethodItem';
import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    paymentMethods: {
        marginTop: theme.spacing(3),
    },
    table: {
        minWidth: '100%',
    },
    tableWrapper: {
        overflowX: 'auto',

        [theme.breakpoints.down('sm')]: {
            overflowX: 'inherit'
        }
    },
    tableCell: {
        whiteSpace: "nowrap"
    },
    invoiceBackground: {
        backgroundColor: theme.palette.grey[100],
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginTop: '24px',
        marginBottom: '0px'
    },
    mobileButton: {
        width: '100%',
        marginTop: theme.spacing(1),
    },
    mobileItem: {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        paddingBottom: theme.spacing(3),
        marginTop: theme.spacing(3),

        '&:last-of-type': {
            borderBottom: '0px',
            paddingBottom: '0px',
        }
    }
}));

export default function Payment(props) {

    const { app, hydrateApp } = useApp();
    const api = useApi();

    const { urlPrefix } = props;

    const [ paymentMethods, setPaymentMethods ] = useState([]);
    const [ defaultMethod, setDefaultMethod ] = useState(null);
    const [ unpaidInvoices, setUnpaidInvoices ] = useState([]);
    const [ payments, setPayments ] = useState([]);

    const [ paymentsPage, setPaymentsPage ] = useState(0);
    const [ activeInvoice, setActiveInvoice ] = useState(null);
    const [ editingMethod, setEditingMethod ] = useState(null);

    const [ savePaymentMethodDialogOpen, setSavePaymentMethodDialogOpen ] = useState(false);
    const [ chargePaymentMethodDialogOpen, setChargePaymentMethodDialogOpen ] = useState(false);
    const [ editPaymentMethodDialogOpen, setEditPaymentMethodDialogOpen ] = useState(false);

    const [ paymentMethodsLoading, setPaymentMethodsLoading ] = useState(false);
    const [ unpaidInvoicesLoading, setUnpaidInvoicesLoading ] = useState(false);
    const [ paymentsLoading, setPaymentsLoading ] = useState(false);

    const classes = useStyles();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Getters --------------------------------------

    const getPaymentMethods = () => {
        setPaymentMethodsLoading(true);

        api.get(urlPrefix + '/payment/method')
            .then(res => {
                setPaymentMethods(res.methods);
                setDefaultMethod(res.default_payment_method)
            }).finally(() => setPaymentMethodsLoading(false));
    }

    const getUnpaidInvoices = () => {
        setUnpaidInvoicesLoading(true);

        api.get(urlPrefix + '/payment/invoices/unpaid')
            .then(res => setUnpaidInvoices(res.invoices))
            .finally(() => setUnpaidInvoicesLoading(false));
    }

    const getPayments = () => {
        setPaymentsLoading(true);

        api.get(urlPrefix + '/payment/payments')
            .then(res => setPayments(res.payments))
            .finally(() => setPaymentsLoading(false));
    }

    useEffect(() => {
        getPaymentMethods();
        getUnpaidInvoices();
        getPayments();
    }, []);

    // Handlers -------------------------

    const handleChangePaymentsPage = (event, newPage) => {
        setPaymentsPage(newPage);
    }

    // Posters --------------------------------------

    const postSendReceipt = id => {
        setPaymentsLoading(true);

        api.post(urlPrefix + `/payment/payment/${id}/receipt`)
            .finally(() => setPaymentsLoading(false));
    }

    // Save Card Dialog --------------------------------------

    const openSavePaymentMethodDialog = () => {
        setSavePaymentMethodDialogOpen(true);
    }

    const closeSavePaymentMethodDialog = () => {
        setSavePaymentMethodDialogOpen(false);
    }

    const closeSavePaymentMethodDialogAndRefresh = () => {
        closeSavePaymentMethodDialog();
        getPaymentMethods();
    }

    // Edit Payment Method Dialog --------------------------------------

    const openEditPaymentMethodDialog = (method) => {
        setEditingMethod(method);
        setEditPaymentMethodDialogOpen(true);
    }

    const closeEditPaymentMethodDialog = () => {
        setEditPaymentMethodDialogOpen(false);
    }

    const closeEditPaymentMethodDialogAndRefresh = () => {
        closeEditPaymentMethodDialog();
        getPaymentMethods();
    }

    const editPaymentMethodDialogClosed = () => {
        setEditingMethod(null);
    }

    // Charge Card Dialog --------------------------------------

    const openChargePaymentMethodDialog = invoice => {
        setActiveInvoice(invoice);
        setChargePaymentMethodDialogOpen(true);
    }

    const closeChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(false);
    }

    const closeChargePaymentMethodDialogAndRefresh = () => {
        closeChargePaymentMethodDialog();
        hydrateApp(true);
        getUnpaidInvoices();
        getPayments();
    }

    const chargePaymentMethodDialogClosed = () => {
        setActiveInvoice(null);
    }

    // Utils --------------------------------------

    const emptyRows = 10 - payments.slice((paymentsPage * 10), (paymentsPage * 10) + 10).length;
    const onlinePaymentEnabled = (app.club.meta.payment_methods.includes('stripe_ach') || app.club.meta.payment_methods.includes('stripe'));

    return (
        <form
            noValidate
            autoComplete="off"
            >
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Payment Information
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Here you can view / edit your saved payment methods, view and pay any unpaid invoices, as well as view previous invoices across all of your connected clubs.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={paymentMethodsLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Payment Methods
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={openSavePaymentMethodDialog}
                            startIcon={<CreditCardIcon />}
                            >
                            Add Payment Method
                        </Button>
                    </Grid>
                </Grid>
                { paymentMethods.length ? (
                    <List className={classes.paymentMethods} disablePadding dense>
                        {paymentMethods.map(method => <PaymentMethodItem key={method.id} method={method} defaultMethod={defaultMethod} openEditPaymentMethodDialog={openEditPaymentMethodDialog} />)}
                    </List>
                ) : (
                    <Typography variant="body2" component="h5" color="textSecondary" gutterBottom>
                        No saved payment methods.
                    </Typography>
                )}
                {app.club.meta.payment_methods.includes('check') &&
                    <div className={classes.paymentMethods}>
                        <Divider/>
                        <Typography variant="subtitle2" className={classes.paymentMethods} gutterBottom>
                            To pay by check, please send payment to the following address:
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            component="div"
                            className={classes.checkAddress}
                            >
                            {app.club.meta.check_name}<br/>
                            {app.club.meta.check_address_one} {app.club.meta.check_address_two ?? ''}<br/>
                            {app.club.meta.check_city}, {app.club.meta.check_state} {app.club.meta.check_zip}

                        </Typography>
                    </div>
                }
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={unpaidInvoicesLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Unpaid Invoices
                        </Typography>
                    </Grid>
                </Grid>
                { unpaidInvoices.length ? (
                    <div className={classes.tableWrapper}>
                        { isMobile ? unpaidInvoices.map(invoice => (
                            <div className={classes.mobileItem} key={invoice.id}>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    >
                                    {invoice.club.name} - {invoice.description}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    color="primary"
                                    display="inline"
                                    >
                                    ${invoice.amount_f}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    color={DateTime.fromISO(invoice.due_by) < DateTime.local() ?"error" : "textSecondary"}
                                    display="inline"
                                    gutterBottom
                                    >
                                    &nbsp;-&nbsp;Due&nbsp;{DateTime.fromISO(invoice.due_by).toLocaleString(DateTime.DATE_MED)}
                                </Typography>
                                {invoice.processing ? (
                                    <Tooltip
                                        title={'Your payment for this invoice is pending. We will email you once the status of this payment changes.'}
                                        aria-label="pending">
                                        <span>
                                            <Button
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                                disabled
                                                className={classes.mobileButton}
                                                >
                                                PENDING
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={onlinePaymentEnabled ? 'Pay Invoice' : `Payments disabled for ${invoice.club.name}`}
                                        aria-label="pay"
                                        >
                                        <span>
                                            <Button
                                                size="small"
                                                color="primary"
                                                onClick={() => openChargePaymentMethodDialog(invoice)}
                                                variant="outlined"
                                                disabled={!onlinePaymentEnabled}
                                                className={classes.mobileButton}
                                                >
                                                PAY
                                            </Button>
                                        </span>

                                    </Tooltip>
                                )}
                            </div>
                        )) : (
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableCell}>
                                            Organization
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Invoice #
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Description
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Due On
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            Amount
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { unpaidInvoices.map(invoice => (
                                        <TableRow key={invoice.id}>
                                            <TableCell
                                                className={classes.tableCell}
                                                component="th"
                                                scope="row"
                                                >
                                                {invoice.club.name}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {invoice.invoice_number}
                                            </TableCell>
                                            <TableCell>
                                                {invoice.description}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Typography
                                                    variant="subtitle2"
                                                    color={DateTime.fromISO(invoice.due_by) < DateTime.local() ?"error" : "textSecondary"}
                                                    >
                                                    {DateTime.fromISO(invoice.due_by).toLocaleString(DateTime.DATE_MED)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                ${invoice.amount_f}
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                {invoice.processing ? (
                                                    <Tooltip
                                                        title={'Your payment for this invoice is pending. We will email you once the status of this payment changes.'}
                                                        aria-label="pending">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                                disabled
                                                                >
                                                                PENDING
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip
                                                        title={onlinePaymentEnabled ? 'Pay Invoice' : `Online payments disabled for ${invoice.club.name}`}
                                                        aria-label="pay">
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => openChargePaymentMethodDialog(invoice)}
                                                            variant="outlined"
                                                            disabled={!onlinePaymentEnabled}
                                                            >
                                                            PAY
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                ) : (
                    <Typography variant="body2" component="h5" color="textSecondary" gutterBottom>
                        No unpaid invoices.
                    </Typography>
                )}
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={paymentsLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Previous Payments
                        </Typography>
                    </Grid>
                </Grid>
                { payments.length ? (
                    <div className={classes.tableWrapper}>
                        { isMobile ? payments.slice((paymentsPage * 10), (paymentsPage * 10) + 10).map(payment => (
                            <div className={classes.mobileItem} key={payment.id}>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    >
                                    {payment.invoice.club.name} - {payment.invoice.description}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    color="primary"
                                    display="inline"
                                    >
                                    ${payment.amount_f}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    color="textSecondary"
                                    gutterBottom
                                    display="inline"
                                    >
                                    &nbsp;-&nbsp;Paid&nbsp;{DateTime.fromISO(payment.paid_at).toLocaleString(DateTime.DATE_MED)}
                                </Typography>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={() => postSendReceipt(payment.id)}
                                    variant="outlined"
                                    className={classes.mobileButton}
                                    >
                                    Email Receipt
                                </Button>
                            </div>
                        )) : (
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableCell}>
                                            Organization
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Invoice #
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Description
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            Amount
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            Date Paid
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            Receipt
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { payments.slice((paymentsPage * 10), (paymentsPage * 10) + 10).map(payment => (
                                        <TableRow hover key={payment.id}>
                                            <TableCell
                                                className={classes.tableCell}
                                                component="th"
                                                scope="row"
                                                >
                                                {payment.invoice.club.name}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {payment.invoice.invoice_number}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {payment.invoice.description}
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                ${payment.amount_f}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {DateTime.fromISO(payment.paid_at).toLocaleString(DateTime.DATE_MED)}
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                <Tooltip
                                                    title='Email Receipt'
                                                    aria-label="email receipt"
                                                    >
                                                    <IconButton
                                                        onClick={() => postSendReceipt(payment.id)}
                                                        >
                                                        <ReceiptIcon size="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(emptyRows > 0) &&
                                        <TableRow style={{ height: 81 * emptyRows }}>
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        )}
                        <TablePagination
                            component="div"
                            count={payments.length}
                            rowsPerPage={10}
                            rowsPerPageOptions={[10]}
                            page={paymentsPage}
                            onChangePage={handleChangePaymentsPage}
                        />
                    </div>
                ) : (
                    <Typography variant="body2" component="h5" color="textSecondary" gutterBottom>
                        No previous payments.
                    </Typography>
                )}
            </Paper>
            <SavePaymentMethodDialog
                user={app.user}
                open={savePaymentMethodDialogOpen}
                onSave={closeSavePaymentMethodDialogAndRefresh}
                onClose={closeSavePaymentMethodDialog}
            />
            <EditPaymentMethodDialog
                user={app.user}
                open={editPaymentMethodDialogOpen}
                onSave={closeEditPaymentMethodDialogAndRefresh}
                onClose={closeEditPaymentMethodDialog}
                onClosed={editPaymentMethodDialogClosed}
                method={editingMethod}
                urlPrefix={urlPrefix}
                defaultMethodId={defaultMethod}
            />
            <ChargePaymentMethodDialog
                user={app.user}
                club={activeInvoice ? activeInvoice.club : null}
                invoice={activeInvoice}
                urlPrefix={urlPrefix}
                open={chargePaymentMethodDialogOpen}
                onClose={closeChargePaymentMethodDialog}
                onClosed={chargePaymentMethodDialogClosed}
                onCharge={closeChargePaymentMethodDialogAndRefresh}
            />
        </form>
    )
}
